/**
 * WS-C §3.3 — the OAuth code exchange, landing INSIDE this SPA.
 *
 * ⛔ `navigate`, not `location.replace`. The destination is inside this SPA now — that is the
 * single line the void D-8 existed to work around, and D-8′ (fixing the return target
 * server-side in `cp-auth-api`) makes it a plain in-app navigation.
 *
 * ⛔ THE STASH IS CONSUMED HERE, AND IT ARRIVES ROUTER-RELATIVE. `takeReturnTo()` stores a
 * browser-absolute path and strips the basename on the way out, so BOTH arms of the `??` below are
 * the same coordinate system. Handing `navigate()` a browser path would produce
 * `/cp-admin/cp-admin/apps/X` — the A2 defect, where the spec's test contradicted the spec's code.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { displayMessage } from '../platform/errors';
import { takeReturnTo as takeReturnToDefault } from '../platform/auth/return-to';
import { AuthCardFrame, AuthPendingState } from '../shell/AuthCardFrame';
import { ButtonLink } from '../shell/ButtonLink';

export interface OAuthCallbackPageProps {
  readonly onExchangeCode: (code: string) => Promise<void>;
  /**
   * Consumes the §4 stash exactly once, returning a ROUTER-RELATIVE path or `null`.
   *
   * Injected rather than imported so a test can drive both arms without touching `sessionStorage` —
   * a plain closure, never `vi.fn()`. Defaults to the real one, so production wiring is not optional.
   */
  readonly takeReturnTo?: () => string | null;
}

export function OAuthCallbackPage({
  onExchangeCode,
  takeReturnTo = takeReturnToDefault,
}: OAuthCallbackPageProps): React.JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get('code');
  const [error, setError] = useState<string | null>(null);
  /*
   * ⛔ A single-use auth code must be exchanged EXACTLY ONCE. Under `<StrictMode>` React
   * deliberately runs an effect twice in development, and a second exchange of a consumed code
   * fails — which would render the terminal error card on a login that actually succeeded. The
   * ref is checked and set synchronously, so both invocations see it.
   */
  const exchanged = useRef(false);

  useEffect(() => {
    if (code === null || exchanged.current) return;
    exchanged.current = true;
    onExchangeCode(code)
      .then(() => {
        /*
         * ⛔ `{ replace: true }` — and it is not cosmetic. It strips `?code=` from the address
         * bar, and a single-use auth code sitting in history is a code sitting in `Referer` on the
         * next outbound link. It also stops the back button from re-entering a consumed callback.
         */
        // ⛔ Consumed here, not at mount: a failed exchange must LEAVE the stash intact so the
        // retry through /login still resumes the original destination.
        void navigate(takeReturnTo() ?? '/', { replace: true });
      })
      .catch((e: unknown) => {
        setError(displayMessage(e, 'Could not complete sign-in.'));
      });
  }, [code, navigate, onExchangeCode, takeReturnTo]);

  // Absent `code` ⇒ a terminal error, and NOTHING is exchanged. This is the state someone reaches
  // by opening the callback URL directly or by a provider redirect that dropped the parameter.
  if (code === null || error !== null) {
    /*
     * ⚠️ **`role="alert"` NOW SITS ON THE NOTICE, NOT ON THE WHOLE CARD** — and that is an improvement
     * rather than a transcription. The old card wrapped the heading, the sentence AND the retry link in
     * one assertive live region, so a screen reader announced a link as part of the alarm. The frame
     * puts the assertive region on the sentence that IS the failure and leaves the heading and the
     * action as ordinary content.
     *
     * ⚠️ `aria-atomic`/`aria-live` are not re-added: `AuthCardFrame` sets `role="alert"` for a `bad`
     * verdict and deliberately no `aria-live`, because a `role="alert"` carrying `aria-live="polite"`
     * is two contradictory instructions on one element.
     *
     * ⛔ The `code === null` arm still exchanges NOTHING — the effect above returns before touching
     * `onExchangeCode`. This is what somebody reaches by opening the callback URL directly, or by a
     * provider redirect that dropped the parameter.
     */
    return (
      <AuthCardFrame
        title="Sign-in didn’t complete"
        notice={{
          verdict: 'bad',
          message:
            code === null
              ? 'This page was opened without an authorization code, so there was nothing to complete.'
              : error,
        }}
        testId="oauth-callback-error"
      >
        {/* ⛔ A real anchor, not a `<button onClick={navigate}>`: `/login` has a URL, so right-click,
            middle-click and copy-link all keep working, and it announces as a link.

            ⛔ `Try again` on `primary`, matching `SignInIncompletePage` BYTE FOR BYTE (#2496, D-2
            and D-4). That page and this card are the same event seen from either side of the
            redirect — a sign-in that did not complete — and the server picks which of the two a
            person lands on. Two labels and two tones for one event teaches an operator triaging a
            report that they are looking at two different failures. */}
        <ButtonLink to="/login" tone="primary" size="md" data-testid="oauth-callback-retry">
          Try again
        </ButtonLink>
      </AuthCardFrame>
    );
  }

  /* ⛔ The SAME component the `auth.isLoading` splash renders — the two spinners were byte-identical
     literals in two files, and the frame they sat in was not (this one had no `p-8`). */
  return <AuthPendingState label="Completing sign-in…" testId="oauth-callback-pending" />;
}
